/*
 * reports-b scope-access helpers (BD-901).
 *
 * Pure logic for the locked, self-scoped reports-b experience shown to a
 * regular user (role === '') who has been given a reportingLevel. These
 * helpers only decide what the UI SAYS and whether to lock it; the server is
 * the real enforcer of the data scope (it derives and clamps scope from the
 * caller's identity). Keeping the derivation here — free of React/Redux — lets
 * it be unit-tested and reused by the shell without a render.
 *
 * reportingLevel is one of the strings the portal assigns:
 *   'Tenancy' | 'Line manager' | 'Department' | 'Location' | 'City' |
 *   'Country' | 'Company Name' | 'State'
 * (null / '' means the user has no reporting access at all).
 */

// Maps a dimension-style reportingLevel to the field on the user record that
// holds that user's own value for the dimension (see AddUserModal / UsersTable).
export const REPORTING_DIM_FIELDS = {
    Department: "department",
    Location: "location",
    City: "city",
    Country: "country",
    State: "state",
    "Company Name": "companyName"
};

// A "scoped user" is a regular user (no admin role) who has been assigned a
// reporting level. These users reach reports-b but are locked to their own
// slice. Admins and tenancy admins are never scoped.
export function isScopedReportingUser({ role, reportingLevel } = {}) {
    if ((role || "") !== "") return false;
    return reportingLevel != null && reportingLevel !== "";
}

// Builds the read-only banner text for a scoped user, e.g.
//   Line manager            -> "Scoped to: your team"
//   Department (value)      -> "Scoped to: Department: Finance"
//   Department (no value)   -> "Scoped to: your department"
//   Tenancy (name known)    -> "Scoped to: Acme Ltd"
//   Tenancy (name unknown)  -> "Scoped to: your tenancy"
// Returns '' when there is no reporting level (nothing to lock to).
export function buildScopeBanner({ reportingLevel, user = {}, tenancyName = "" } = {}) {
    if (reportingLevel == null || reportingLevel === "") return "";
    const level = String(reportingLevel).trim();

    // Line manager: scoped to the team they manage — there is no single
    // attribute value, so name the relationship rather than a dimension value.
    if (level === "Line manager" || level === "LineManager") {
        return "Scoped to: your team";
    }

    if (level === "Tenancy") {
        return tenancyName ? `Scoped to: ${tenancyName}` : "Scoped to: your tenancy";
    }

    const field = REPORTING_DIM_FIELDS[level];
    if (field) {
        const value = user ? user[field] : null;
        if (value) return `Scoped to: ${level}: ${value}`;
        return `Scoped to: your ${level.toLowerCase()}`;
    }

    // Unknown / future level: still lock, but keep the wording safe and generic.
    return "Scoped to: your data";
}
